/**
 * Password reset page for Aegix One.
 *
 * Two modes:
 * 1. Request reset: user enters email to receive a reset link
 * 2. Reset with key: user sets a new password using the link from email
 */

import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Flex,
  Text,
  Input,
  InputGroup,
  Image,
  VStack,
  Link,
  IconButton,
  chakra,
} from '@chakra-ui/react';
import { HiEye, HiEyeSlash } from 'react-icons/hi2';
import { Button } from '@themes/aegix';
import { useColorMode } from '@/components/ui/color-mode';
import logoLight from '@/assets/aegix-one-logo-light.svg';
import logoDark from '@/assets/aegix-one-logo-dark.svg';
import {
  requestResetSchema,
  resetPasswordSchema,
  type RequestResetFormValues,
  type ResetPasswordFormValues,
} from '@/validation/auth';
import { useResetPasswordForm } from '@/hooks/useResetPasswordForm';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export default function ResetPassword() {
  const { t } = useTranslation();
  useDocumentTitle(t('resetPassword.title'));
  const { colorMode } = useColorMode();
  const {
    resetKey,
    error,
    requestLoading,
    requestSent,
    handleRequestReset,
    showPassword,
    setShowPassword,
    resetLoading,
    resetDone,
    handleResetPassword,
  } = useResetPasswordForm();

  const requestForm = useForm<RequestResetFormValues>({
    resolver: zodResolver(requestResetSchema),
    mode: 'onBlur',
    defaultValues: { email: '' },
  });

  const resetForm = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onBlur',
    defaultValues: { password: '', confirmPassword: '' },
  });

  // Get the email value for the success message
  const requestedEmail = requestForm.getValues('email');

  return (
    <Flex
      height="100vh"
      alignItems="center"
      justifyContent="center"
      bg="surface.primary"
    >
      <Box
        maxW="400px"
        w="100%"
        p={8}
        borderWidth="1px"
        borderColor="border.primary"
        borderRadius="lg"
        bg="surface.primary"
        boxShadow="0px 2.5px 4px -1px rgba(0, 0, 0, 0.15)"
      >
        <VStack gap={6}>
          <Image
            src={colorMode === 'dark' ? logoDark : logoLight}
            alt="Aegix One"
            w="200px"
          />

          {/* Mode 1: Request password reset */}
          {!resetKey && !requestSent && (
            <>
              <Text fontSize="lg" fontWeight="semibold" color="text.primary">
                {t('resetPassword.requestTitle')}
              </Text>
              <Text
                fontSize="sm"
                color="text.secondary"
                textAlign="center"
              >
                {t('resetPassword.requestSubtitle')}
              </Text>

              <form
                onSubmit={requestForm.handleSubmit(handleRequestReset)}
                style={{ width: '100%' }}
              >
                <VStack gap={4} w="100%">
                  <Box w="100%">
                    <chakra.label htmlFor="reset-email" fontSize="sm" color="text.secondary" mb={1} display="block">
                      {t('login.emailLabel')}
                    </chakra.label>
                    <Input
                      id="reset-email"
                      type="email"
                      autoComplete="email"
                      aria-invalid={!!requestForm.formState.errors.email}
                      aria-describedby={requestForm.formState.errors.email ? 'reset-email-error' : undefined}
                      {...requestForm.register('email')}
                      placeholder={t('login.emailPlaceholder')}
                      size="md"
                      // eslint-disable-next-line jsx-a11y/no-autofocus -- single-field reset-password modal; focus is the only next action a user can take
                      autoFocus
                    />
                    {requestForm.formState.errors.email && (
                      <Text id="reset-email-error" role="alert" color="red.500" fontSize="sm">
                        {t(requestForm.formState.errors.email.message!)}
                      </Text>
                    )}
                  </Box>

                  {error && (
                    <Text role="alert" aria-live="assertive" color="red.500" fontSize="sm">
                      {error}
                    </Text>
                  )}

                  <Button
                    visual="primary"
                    type="submit"
                    disabled={requestLoading || !requestForm.formState.isValid}
                    style={{ width: '100%' }}
                  >
                    {requestLoading ? t('resetPassword.sending') : t('resetPassword.sendLink')}
                  </Button>
                </VStack>
              </form>
            </>
          )}

          {/* Mode 1 success: Email sent */}
          {!resetKey && requestSent && (
            <>
              <Text fontSize="lg" fontWeight="semibold" color="text.primary">
                {t('resetPassword.checkEmailTitle')}
              </Text>
              <Text
                fontSize="sm"
                color="text.secondary"
                textAlign="center"
              >
                {t('resetPassword.checkEmailDesc', { email: requestedEmail })}
              </Text>
            </>
          )}

          {/* Mode 2: Set new password with key */}
          {resetKey && !resetDone && (
            <>
              <Text fontSize="lg" fontWeight="semibold" color="text.primary">
                {t('resetPassword.setNewTitle')}
              </Text>

              <form
                onSubmit={resetForm.handleSubmit(handleResetPassword)}
                style={{ width: '100%' }}
              >
                <VStack gap={4} w="100%">
                  <Box w="100%">
                    <chakra.label htmlFor="reset-new-password" fontSize="sm" color="text.secondary" mb={1} display="block">
                      {t('resetPassword.newPasswordLabel')}
                    </chakra.label>
                    <InputGroup
                      endElement={
                        <IconButton
                          aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                          onClick={() => setShowPassword(!showPassword)}
                          variant="ghost"
                          size="xs"
                          color="text.secondary"
                        >
                          {showPassword ? <HiEyeSlash /> : <HiEye />}
                        </IconButton>
                      }
                    >
                      <Input
                        id="reset-new-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        aria-invalid={!!resetForm.formState.errors.password}
                        aria-describedby={resetForm.formState.errors.password ? 'reset-password-error' : undefined}
                        {...resetForm.register('password')}
                        placeholder={t('register.passwordPlaceholder')}
                        size="md"
                        // eslint-disable-next-line jsx-a11y/no-autofocus -- password step of the reset flow; focus continues the single user task
                        autoFocus
                      />
                    </InputGroup>
                    {resetForm.formState.errors.password && (
                      <Text id="reset-password-error" role="alert" color="red.500" fontSize="sm">
                        {t(resetForm.formState.errors.password.message!)}
                      </Text>
                    )}
                  </Box>

                  <Box w="100%">
                    <chakra.label htmlFor="reset-confirm-password" fontSize="sm" color="text.secondary" mb={1} display="block">
                      {t('register.confirmPasswordLabel')}
                    </chakra.label>
                    <InputGroup
                      endElement={
                        <IconButton
                          aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                          onClick={() => setShowPassword(!showPassword)}
                          variant="ghost"
                          size="xs"
                          color="text.secondary"
                        >
                          {showPassword ? <HiEyeSlash /> : <HiEye />}
                        </IconButton>
                      }
                    >
                      <Input
                        id="reset-confirm-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        aria-invalid={!!resetForm.formState.errors.confirmPassword}
                        aria-describedby={resetForm.formState.errors.confirmPassword ? 'reset-confirmPassword-error' : undefined}
                        {...resetForm.register('confirmPassword')}
                        placeholder={t('register.confirmPasswordPlaceholder')}
                        size="md"
                      />
                    </InputGroup>
                    {resetForm.formState.errors.confirmPassword && (
                      <Text id="reset-confirmPassword-error" role="alert" color="red.500" fontSize="sm">
                        {t(resetForm.formState.errors.confirmPassword.message!)}
                      </Text>
                    )}
                  </Box>

                  {error && (
                    <Text role="alert" aria-live="assertive" color="red.500" fontSize="sm">
                      {error}
                    </Text>
                  )}

                  <Button
                    visual="primary"
                    type="submit"
                    disabled={resetLoading || !resetForm.formState.isValid}
                    style={{ width: '100%' }}
                  >
                    {resetLoading ? t('resetPassword.resetting') : t('resetPassword.resetBtn')}
                  </Button>
                </VStack>
              </form>
            </>
          )}

          {/* Mode 2 success: Password reset complete */}
          {resetKey && resetDone && (
            <>
              <Text fontSize="lg" fontWeight="semibold" color="green.600">
                {t('resetPassword.doneTitle')}
              </Text>
              <Text fontSize="sm" color="text.secondary" textAlign="center">
                {t('resetPassword.doneDesc')}
              </Text>
              <Link asChild>
                <RouterLink to="/login">
                  <Button visual="primary" style={{ width: '100%' }}>
                    {t('resetPassword.continueSignIn')}
                  </Button>
                </RouterLink>
              </Link>
            </>
          )}

          <Text fontSize="sm" color="text.secondary">
            <Link asChild color="blue.500" fontWeight="medium">
              <RouterLink to="/login">{t('resetPassword.backSignIn')}</RouterLink>
            </Link>
          </Text>
        </VStack>
      </Box>
    </Flex>
  );
}
